/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import React from "react";
import MaterialDispatchBadge from "../../../MaterialRequest/MaterialDispatch/MaterialDispatchBadge";
import MaterialApprovalBadge from "../../../MaterialRequest/Approval/MaterialApprovalBadge";
import {IconClockTable} from "../../../../../../../components/buttons/IconButtons";
import {getStatusConfig} from "../../../../../Visitor/Request/Model/Model";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {sanitizeString} from "../../../../../../../Apis/functions";

/**
 * Result the folio carries at the top of its detail.
 *
 * The dispatch is what closes the movement, so it is the one displayed once it
 * is registered; a folio denied by the approver never reaches the gate open, so
 * its denial is the one that is read instead. A folio that is still waiting for
 * the vehicle says what the gate has to register before the dispatch, and the
 * details that do not wait for anything display nothing.
 *
 * @param {Object} props - Information of the folio.
 * @param {Function} props.t - Translation handler.
 * @param {Object} props.item - Folio the movement belongs to.
 * @param {boolean} props.showBadge - Whether the dispatch of the folio is displayed.
 * @param {string} props.pendingTrans - Text read while the folio waits for the vehicle.
 *
 * @returns {JSX.Element|null} Badge of the result of the folio.
 */
const StatusBadgeSection = ({t, item, showBadge, pendingTrans}) => {
    // What the guard found, who reported it and when are read in the card that
    // details the discrepancies, so the badge of a folio reported with them
    // only names the report
    const isDiscrepancy = item?.dispatchStatusId === STATUS_MODEL.DISCREPANCY;

    if (showBadge)
        return (
            <MaterialDispatchBadge
                t={t}
                showBadge={showBadge}
                dispatchStatusId={item?.dispatchStatusId}
                dispatchReason={isDiscrepancy ? null : sanitizeString(item?.dispatchReason)}
                dispatchDate={item?.dispatchDate}
                dispatchUser={item?.dispatchUser}
                showRegisteredBy={!isDiscrepancy}
            />
        );

    if (item?.approvalStatusId === STATUS_MODEL.DENIED)
        return (
            <MaterialApprovalBadge
                t={t}
                currentStatusConfig={getStatusConfig(item?.approvalStatusId)}
                approvalReason={sanitizeString(item?.approvalReason)}
                approvalUser={item?.approvalUser}
                approvalDate={item?.approvalDate}
            />
        );

    // The movement the gate still has to register is read as an alert of the
    // whole detail, so the notice is written complete and not cut to one row
    if (pendingTrans)
        return (
            <div className="d-flex align-items-center w-100 p-3 mb-3 border-radius-8px background-warning-100 border-full-warning-150">
                <IconClockTable className="icon-color-warning-700 h-24px w-24px mr-3"/>

                <p className="fs-14px ibl-font-w-600 font-color-warning-700 mb-0">
                    {t(pendingTrans)}
                </p>
            </div>
        );

    return null;
};

export default StatusBadgeSection;
